<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
    @click="closeModal"
  >
    <div
      class="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white"
      @click.stop
    >
      <!-- Modal Header -->
      <div
        class="flex items-center justify-between pb-4 border-b border-gray-200"
      >
        <div class="flex items-center space-x-3">
          <div
            class="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center"
          >
            <i class="material-icons text-white text-xl">public</i>
          </div>
          <div>
            <h3 class="text-xl font-semibold text-gray-900">
              Global Distribution Report
            </h3>
            <p class="text-sm text-gray-600">
              Comprehensive analysis of {{ totalCountries }} markets
            </p>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <div class="relative">
            <button
              @click="toggleExportDropdown"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center"
              :disabled="exportLoading"
            >
              <i
                class="material-icons text-sm mr-1"
                :class="{ 'animate-spin': exportLoading }"
              >
                {{ exportLoading ? "sync" : "download" }}
              </i>
              {{ exportLoading ? "Exporting..." : "Export" }}
              <i class="material-icons text-sm ml-1">arrow_drop_down</i>
            </button>

            <!-- Export Dropdown -->
            <div
              v-if="showExportDropdown"
              class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
            >
              <div class="py-1">
                <button
                  @click="exportReport('csv')"
                  class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <i class="material-icons text-sm mr-2">description</i>
                  Export as CSV
                </button>
                <button
                  @click="exportReport('excel')"
                  class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <i class="material-icons text-sm mr-2">table_chart</i>
                  Export as Excel
                </button>
                <button
                  @click="exportReport('json')"
                  class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <i class="material-icons text-sm mr-2">code</i>
                  Export as JSON
                </button>
                <button
                  @click="exportReport('pdf')"
                  class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  disabled
                  title="PDF export coming soon"
                >
                  <i class="material-icons text-sm mr-2 text-gray-400"
                    >picture_as_pdf</i
                  >
                  <span class="text-gray-400">Export as PDF</span>
                  <span class="ml-auto text-xs text-gray-400">Soon</span>
                </button>
              </div>
            </div>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>

      <!-- Modal Content -->
      <div class="py-6 max-h-96 overflow-y-auto">
        <!-- Executive Summary -->
        <div class="mb-8">
          <h4 class="text-lg font-medium text-gray-800 mb-4">
            Executive Summary
          </h4>
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
          >
            <div class="bg-blue-50 rounded-lg p-4 text-center">
              <div class="text-2xl font-bold text-blue-700">
                {{ totalTenants }}
              </div>
              <div class="text-sm text-blue-600">Total Tenants</div>
            </div>
            <div class="bg-green-50 rounded-lg p-4 text-center">
              <div class="text-2xl font-bold text-green-700">
                {{ totalCountries }}
              </div>
              <div class="text-sm text-green-600">Active Markets</div>
            </div>
            <div class="bg-purple-50 rounded-lg p-4 text-center">
              <div class="text-2xl font-bold text-purple-700">
                {{ concentrationLevel }}
              </div>
              <div class="text-sm text-purple-600">Concentration</div>
            </div>
            <div class="bg-orange-50 rounded-lg p-4 text-center">
              <div class="text-2xl font-bold text-orange-700">
                {{ diversityScore }}
              </div>
              <div class="text-sm text-orange-600">Diversity</div>
            </div>
          </div>

          <!-- Key Insights -->
          <div
            class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4"
          >
            <h5 class="font-medium text-gray-800 mb-2">Key Insights</h5>
            <div class="space-y-2">
              <div
                v-for="insight in keyInsights"
                :key="insight.type"
                class="flex items-start space-x-2"
              >
                <i
                  class="material-icons text-sm mt-0.5"
                  :class="insight.iconColor"
                >
                  {{ insight.icon }}
                </i>
                <p class="text-sm text-gray-700">{{ insight.text }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Detailed Country Analysis -->
        <div class="mb-8">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-lg font-medium text-gray-800">
              Country Performance Analysis
            </h4>
            <div class="flex items-center space-x-2">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search countries..."
                class="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                v-model="sortBy"
                class="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="tenants">Sort by Tenants</option>
                <option value="percentage">Sort by Percentage</option>
                <option value="name">Sort by Name</option>
              </select>
            </div>
          </div>

          <!-- Countries Table -->
          <div class="overflow-x-auto border border-gray-200 rounded-lg">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Rank
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Country
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Tenants
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Market Share
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Category
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Growth Potential
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr
                  v-for="(country, index) in filteredAndSortedCountries"
                  :key="country.name"
                  class="hover:bg-gray-50"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div
                        class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        :class="getRankBadgeClass(index)"
                      >
                        {{ index + 1 }}
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <img
                        :src="country.flag"
                        :alt="country.name"
                        class="w-6 h-4 rounded-sm mr-3 object-cover"
                        @error="handleImageError"
                      />
                      <div class="text-sm font-medium text-gray-900">
                        {{ country.name }}
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">
                      {{ country.tenants }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="text-sm text-gray-900 mr-2">
                        {{ country.percentage }}%
                      </div>
                      <div class="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          class="h-2 rounded-full"
                          :style="{
                            width:
                              (country.percentage / maxPercentage) * 100 + '%',
                            backgroundColor: getCountryColor(index)
                          }"
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                      :class="getCategoryBadgeClass(country)"
                    >
                      {{ getMarketCategory(country) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <i
                        class="material-icons text-sm mr-1"
                        :class="getGrowthPotentialColor(country)"
                      >
                        {{ getGrowthPotentialIcon(country) }}
                      </i>
                      <span class="text-sm text-gray-700">
                        {{ getGrowthPotential(country) }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Regional Analysis -->
        <div class="mb-8">
          <h4 class="text-lg font-medium text-gray-800 mb-4">
            Regional Analysis
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Market Concentration -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h5 class="font-medium text-gray-700 mb-3">
                Market Concentration Analysis
              </h5>
              <div class="space-y-3">
                <div
                  v-for="segment in concentrationSegments"
                  :key="segment.label"
                  class="flex items-center justify-between"
                >
                  <div class="flex items-center space-x-2">
                    <div
                      class="w-3 h-3 rounded-full"
                      :style="{ backgroundColor: segment.color }"
                    ></div>
                    <span class="text-sm text-gray-600">{{
                      segment.label
                    }}</span>
                  </div>
                  <div class="text-sm font-medium text-gray-700">
                    {{ segment.percentage }}%
                  </div>
                </div>
              </div>
            </div>

            <!-- Risk Assessment -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h5 class="font-medium text-gray-700 mb-3">Risk Assessment</h5>
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600">Concentration Risk</span>
                  <span
                    class="px-2 py-1 text-xs font-semibold rounded-full"
                    :class="getConcentrationRiskClass()"
                  >
                    {{ getConcentrationRisk() }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600"
                    >Geographic Diversity</span
                  >
                  <span
                    class="px-2 py-1 text-xs font-semibold rounded-full"
                    :class="getDiversityRiskClass()"
                  >
                    {{ diversityScore }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600">Emerging Markets</span>
                  <span class="text-sm font-medium text-gray-700">
                    {{ emergingMarketsCount }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recommendations -->
        <div>
          <h4 class="text-lg font-medium text-gray-800 mb-4">
            Strategic Recommendations
          </h4>
          <div class="space-y-4">
            <div
              v-for="recommendation in recommendations"
              :key="recommendation.priority"
              class="border-l-4 p-4 rounded-r-lg"
              :class="getRecommendationClass(recommendation.priority)"
            >
              <div class="flex items-start space-x-3">
                <i
                  class="material-icons text-lg mt-0.5"
                  :class="getRecommendationIconColor(recommendation.priority)"
                >
                  {{ recommendation.icon }}
                </i>
                <div>
                  <h5 class="font-medium text-gray-800 mb-1">
                    {{ recommendation.title }}
                  </h5>
                  <p class="text-sm text-gray-600 mb-2">
                    {{ recommendation.description }}
                  </p>
                  <div
                    class="flex items-center space-x-4 text-xs text-gray-500"
                  >
                    <span>Priority: {{ recommendation.priority }}</span>
                    <span>Impact: {{ recommendation.impact }}</span>
                    <span>Timeline: {{ recommendation.timeline }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GeographicalDetailsModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    geographicalData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: "",
      sortBy: "tenants",
      showExportDropdown: false,
      exportLoading: false
    };
  },
  mounted() {
    // Add click outside listener for export dropdown
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    // Remove click outside listener
    document.removeEventListener("click", this.handleClickOutside);
  },
  computed: {
    totalTenants() {
      return this.geographicalData.reduce(
        (sum, country) => sum + (country.tenants || 0),
        0
      );
    },

    totalCountries() {
      return this.geographicalData.length;
    },

    sortedCountries() {
      return [...this.geographicalData].sort(
        (a, b) => (b.tenants || 0) - (a.tenants || 0)
      );
    },

    filteredAndSortedCountries() {
      let filtered = this.sortedCountries;

      // Apply search filter
      if (this.searchQuery) {
        filtered = filtered.filter(country =>
          country.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case "name":
            return a.name.localeCompare(b.name);
          case "percentage":
            return (b.percentage || 0) - (a.percentage || 0);
          default:
            return (b.tenants || 0) - (a.tenants || 0);
        }
      });

      return filtered;
    },

    maxPercentage() {
      return Math.max(...this.geographicalData.map(c => c.percentage || 0));
    },

    concentrationLevel() {
      const topCountry = this.sortedCountries[0];
      const concentration = topCountry?.percentage || 0;
      if (concentration >= 50) return "High";
      if (concentration >= 30) return "Medium";
      if (concentration >= 20) return "Balanced";
      return "Distributed";
    },

    diversityScore() {
      if (this.totalCountries <= 3) return "Low";
      if (this.totalCountries >= 15) return "High";
      if (this.totalCountries >= 8) return "Medium";
      return "Low";
    },

    emergingMarketsCount() {
      return this.geographicalData.filter(
        c => c.percentage < 2 && c.tenants > 0
      ).length;
    },

    concentrationSegments() {
      const top3 = this.sortedCountries.slice(0, 3);
      const others = this.sortedCountries.slice(3);
      const othersPercentage = others.reduce(
        (sum, c) => sum + (c.percentage || 0),
        0
      );

      const segments = [
        ...top3.map((country, index) => ({
          label: country.name,
          percentage: country.percentage,
          color: this.getCountryColor(index)
        }))
      ];

      if (othersPercentage > 0) {
        segments.push({
          label: `Other Markets (${others.length})`,
          percentage: othersPercentage.toFixed(1),
          color: "#9CA3AF"
        });
      }

      return segments;
    },

    keyInsights() {
      const insights = [];
      const sorted = this.sortedCountries;

      if (sorted.length === 0) return insights;

      // Market dominance insight
      if (sorted[0]?.percentage >= 40) {
        insights.push({
          type: "dominance",
          icon: "analytics",
          iconColor: "text-blue-500",
          text: `${sorted[0].name} dominates with ${sorted[0].percentage}% market share, indicating strong regional presence.`
        });
      }

      // Diversity insight
      if (this.totalCountries >= 15) {
        insights.push({
          type: "diversity",
          icon: "public",
          iconColor: "text-green-500",
          text: `Strong global footprint across ${this.totalCountries} countries demonstrates international market penetration.`
        });
      }

      // Growth opportunity
      if (this.emergingMarketsCount >= 5) {
        insights.push({
          type: "opportunity",
          icon: "trending_up",
          iconColor: "text-purple-500",
          text: `${this.emergingMarketsCount} emerging markets present significant expansion opportunities.`
        });
      }

      return insights;
    },

    recommendations() {
      const recommendations = [];
      const sorted = this.sortedCountries;

      if (sorted.length === 0) return recommendations;

      // High concentration risk
      if (sorted[0]?.percentage >= 50) {
        recommendations.push({
          priority: "High",
          title: "Reduce Market Concentration Risk",
          description: `${sorted[0].name} represents ${sorted[0].percentage}% of your tenant base. Diversify into other markets to reduce dependency.`,
          impact: "High",
          timeline: "3-6 months",
          icon: "warning"
        });
      }

      // Expansion opportunity
      if (this.emergingMarketsCount >= 3) {
        recommendations.push({
          priority: "Medium",
          title: "Scale Emerging Markets",
          description: `Focus investment and marketing efforts on ${this.emergingMarketsCount} emerging markets showing early adoption.`,
          impact: "Medium",
          timeline: "6-12 months",
          icon: "rocket_launch"
        });
      }

      // Market consolidation
      if (sorted.length >= 10 && sorted[4]?.percentage < 5) {
        recommendations.push({
          priority: "Low",
          title: "Consolidate Small Markets",
          description:
            "Consider consolidating or optimizing operations in markets with less than 5% share.",
          impact: "Medium",
          timeline: "12+ months",
          icon: "merge_type"
        });
      }

      return recommendations;
    }
  },

  methods: {
    closeModal() {
      this.showExportDropdown = false;
      this.$emit("close");
    },

    toggleExportDropdown() {
      this.showExportDropdown = !this.showExportDropdown;
    },

    exportReport(format = "csv") {
      this.exportLoading = true;
      this.showExportDropdown = false;

      try {
        // Generate comprehensive report data
        const reportData = this.generateReportData();

        let content, filename, mimeType;

        switch (format) {
          case "csv":
            content = this.generateCSVContent(reportData);
            filename = "geographical-distribution-report.csv";
            mimeType = "text/csv;charset=utf-8;";
            break;

          case "excel":
            content = this.generateExcelContent(reportData);
            filename = "geographical-distribution-report.csv"; // Excel-compatible CSV
            mimeType = "text/csv;charset=utf-8;";
            break;

          case "json":
            content = this.generateJSONContent(reportData);
            filename = "geographical-distribution-report.json";
            mimeType = "application/json;charset=utf-8;";
            break;

          default:
            throw new Error("Unsupported export format");
        }

        // Create and download the file
        this.downloadReport(content, filename, mimeType);

        // Emit completion event
        this.$emit("export-completed");

        // Export completed successfully
      } catch (error) {
        // Export failed, show user notification
        alert("Export failed. Please try again.");
      } finally {
        this.exportLoading = false;
      }
    },

    generateReportData() {
      const timestamp = new Date().toISOString();

      return {
        metadata: {
          reportTitle: "Global Distribution Report",
          generatedAt: timestamp,
          totalTenants: this.totalTenants,
          totalCountries: this.totalCountries,
          concentrationLevel: this.concentrationLevel,
          diversityScore: this.diversityScore,
          emergingMarketsCount: this.emergingMarketsCount
        },
        countries: this.sortedCountries.map((country, index) => ({
          rank: index + 1,
          country: country.name,
          tenants: country.tenants,
          percentage: country.percentage,
          marketCategory: this.getMarketCategory(country),
          growthPotential: this.getGrowthPotential(country),
          concentrationRisk: index === 0 ? this.getConcentrationRisk() : "Low"
        })),
        insights: this.keyInsights.map(insight => ({
          type: insight.type,
          description: insight.text
        })),
        recommendations: this.recommendations.map(rec => ({
          priority: rec.priority,
          title: rec.title,
          description: rec.description,
          impact: rec.impact,
          timeline: rec.timeline
        })),
        concentrationAnalysis: this.concentrationSegments.map(segment => ({
          market: segment.label,
          percentage: segment.percentage
        }))
      };
    },

    generateCSVContent(reportData) {
      let csvContent = "";

      // Add report header
      csvContent += `Global Distribution Report\n`;
      csvContent += `Generated: ${new Date(
        reportData.metadata.generatedAt
      ).toLocaleString()}\n`;
      csvContent += `\n`;

      // Add executive summary
      csvContent += `EXECUTIVE SUMMARY\n`;
      csvContent += `Total Tenants,${reportData.metadata.totalTenants}\n`;
      csvContent += `Total Countries,${reportData.metadata.totalCountries}\n`;
      csvContent += `Concentration Level,${reportData.metadata.concentrationLevel}\n`;
      csvContent += `Diversity Score,${reportData.metadata.diversityScore}\n`;
      csvContent += `Emerging Markets,${reportData.metadata.emergingMarketsCount}\n`;
      csvContent += `\n`;

      // Add country data
      csvContent += `COUNTRY PERFORMANCE ANALYSIS\n`;
      csvContent += `Rank,Country,Tenants,Market Share (%),Category,Growth Potential,Risk Level\n`;

      reportData.countries.forEach(country => {
        csvContent += `${country.rank},"${country.country}",${country.tenants},${country.percentage},"${country.marketCategory}","${country.growthPotential}","${country.concentrationRisk}"\n`;
      });

      csvContent += `\n`;

      // Add market concentration analysis
      csvContent += `MARKET CONCENTRATION ANALYSIS\n`;
      csvContent += `Market,Percentage\n`;

      reportData.concentrationAnalysis.forEach(segment => {
        csvContent += `"${segment.market}",${segment.percentage}\n`;
      });

      csvContent += `\n`;

      // Add key insights
      csvContent += `KEY INSIGHTS\n`;
      csvContent += `Type,Description\n`;

      reportData.insights.forEach(insight => {
        csvContent += `"${insight.type}","${insight.description}"\n`;
      });

      csvContent += `\n`;

      // Add recommendations
      csvContent += `STRATEGIC RECOMMENDATIONS\n`;
      csvContent += `Priority,Title,Description,Impact,Timeline\n`;

      reportData.recommendations.forEach(rec => {
        csvContent += `"${rec.priority}","${rec.title}","${rec.description}","${rec.impact}","${rec.timeline}"\n`;
      });

      return csvContent;
    },

    generateExcelContent(reportData) {
      // Generate Excel-optimized CSV with better formatting
      let csvContent = "";

      // Add report header with enhanced formatting
      csvContent += `"Global Distribution Report",,,,,,\n`;
      csvContent += `"Generated:",${new Date(
        reportData.metadata.generatedAt
      ).toLocaleString()},,,,\n`;
      csvContent += `,,,,,,\n`;

      // Add executive summary in a more structured format
      csvContent += `"EXECUTIVE SUMMARY",,,,,,\n`;
      csvContent += `"Metric","Value",,,,\n`;
      csvContent += `"Total Tenants",${reportData.metadata.totalTenants},,,,\n`;
      csvContent += `"Total Countries",${reportData.metadata.totalCountries},,,,\n`;
      csvContent += `"Concentration Level","${reportData.metadata.concentrationLevel}",,,,\n`;
      csvContent += `"Diversity Score","${reportData.metadata.diversityScore}",,,,\n`;
      csvContent += `"Emerging Markets",${reportData.metadata.emergingMarketsCount},,,,\n`;
      csvContent += `,,,,,,\n`;

      // Add country data with enhanced formatting
      csvContent += `"COUNTRY PERFORMANCE ANALYSIS",,,,,,\n`;
      csvContent += `"Rank","Country","Tenants","Market Share (%)","Category","Growth Potential","Risk Level"\n`;

      reportData.countries.forEach(country => {
        csvContent += `${country.rank},"${country.country}",${country.tenants},${country.percentage},"${country.marketCategory}","${country.growthPotential}","${country.concentrationRisk}"\n`;
      });

      csvContent += `,,,,,,\n`;

      // Add market concentration analysis
      csvContent += `"MARKET CONCENTRATION ANALYSIS",,,,,,\n`;
      csvContent += `"Market","Percentage (%)",,,,\n`;

      reportData.concentrationAnalysis.forEach(segment => {
        csvContent += `"${segment.market}",${segment.percentage},,,,\n`;
      });

      csvContent += `,,,,,,\n`;

      // Add insights and recommendations in structured format
      if (reportData.insights.length > 0) {
        csvContent += `"KEY INSIGHTS",,,,,,\n`;
        csvContent += `"Type","Description",,,,\n`;

        reportData.insights.forEach(insight => {
          csvContent += `"${insight.type}","${insight.description}",,,,\n`;
        });

        csvContent += `,,,,,,\n`;
      }

      if (reportData.recommendations.length > 0) {
        csvContent += `"STRATEGIC RECOMMENDATIONS",,,,,,\n`;
        csvContent += `"Priority","Title","Description","Impact","Timeline",\n`;

        reportData.recommendations.forEach(rec => {
          csvContent += `"${rec.priority}","${rec.title}","${rec.description}","${rec.impact}","${rec.timeline}",\n`;
        });
      }

      return csvContent;
    },

    generateJSONContent(reportData) {
      // Generate pretty-printed JSON
      return JSON.stringify(reportData, null, 2);
    },

    downloadReport(content, filename, mimeType = "text/csv;charset=utf-8;") {
      // Create blob with UTF-8 BOM for proper Excel compatibility
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + content], { type: mimeType });

      // Create download link
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      this.$emit("export-completed");
      // Export completed successfully
    },

    getCountryColor(index) {
      const colors = [
        "#3B82F6",
        "#10B981",
        "#F59E0B",
        "#EF4444",
        "#8B5CF6",
        "#EC4899"
      ];
      return colors[index % colors.length];
    },

    getRankBadgeClass(index) {
      if (index === 0) return "bg-yellow-500"; // Gold
      if (index === 1) return "bg-gray-400"; // Silver
      if (index === 2) return "bg-yellow-600"; // Bronze
      return "bg-blue-500"; // Regular
    },

    getMarketCategory(country) {
      if (country.percentage >= 20) return "Major Market";
      if (country.percentage >= 10) return "Key Market";
      if (country.percentage >= 5) return "Growing Market";
      return "Emerging Market";
    },

    getCategoryBadgeClass(country) {
      if (country.percentage >= 20) return "bg-blue-100 text-blue-800";
      if (country.percentage >= 10) return "bg-green-100 text-green-800";
      if (country.percentage >= 5) return "bg-yellow-100 text-yellow-800";
      return "bg-gray-100 text-gray-800";
    },

    getGrowthPotential(country) {
      if (country.percentage < 2) return "High";
      if (country.percentage < 5) return "Medium";
      if (country.percentage < 15) return "Moderate";
      return "Saturated";
    },

    getGrowthPotentialIcon(country) {
      if (country.percentage < 2) return "trending_up";
      if (country.percentage < 5) return "show_chart";
      if (country.percentage < 15) return "timeline";
      return "insights";
    },

    getGrowthPotentialColor(country) {
      if (country.percentage < 2) return "text-green-500";
      if (country.percentage < 5) return "text-blue-500";
      if (country.percentage < 15) return "text-yellow-500";
      return "text-gray-500";
    },

    getConcentrationRisk() {
      const concentration = this.sortedCountries[0]?.percentage || 0;
      if (concentration >= 60) return "Very High";
      if (concentration >= 40) return "High";
      if (concentration >= 25) return "Medium";
      return "Low";
    },

    getConcentrationRiskClass() {
      const risk = this.getConcentrationRisk();
      if (risk === "Very High") return "bg-red-100 text-red-800";
      if (risk === "High") return "bg-orange-100 text-orange-800";
      if (risk === "Medium") return "bg-yellow-100 text-yellow-800";
      return "bg-green-100 text-green-800";
    },

    getDiversityRiskClass() {
      if (this.diversityScore === "High") return "bg-green-100 text-green-800";
      if (this.diversityScore === "Medium")
        return "bg-yellow-100 text-yellow-800";
      return "bg-red-100 text-red-800";
    },

    getRecommendationClass(priority) {
      if (priority === "High") return "border-red-400 bg-red-50";
      if (priority === "Medium") return "border-yellow-400 bg-yellow-50";
      return "border-blue-400 bg-blue-50";
    },

    getRecommendationIconColor(priority) {
      if (priority === "High") return "text-red-500";
      if (priority === "Medium") return "text-yellow-500";
      return "text-blue-500";
    },

    handleImageError(event) {
      event.target.style.display = "none";
    },

    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showExportDropdown = false;
      }
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar for modal content */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
